<!-- BagFront — a coffee's front, drawn the same way everywhere: on the wall, at
     the top of the Label sheet, in the shopping bag and in lists.

     It is one of three things (src/lib/wall-look.js `frontOf`):
       cut      the real bag, cut out with alpha
       crop     the real bag's product crop, its hairline edge clipped
       sticker  the roaster's photo printed as a die-cut label on a blank pouch

     The root is a block that keeps the front's aspect ratio and fills its
     width by default. To size it by height instead, give it a class with
     `height: …; width: auto`.

     Props
       coffee   the catalogue coffee (needs slug + roasterSlug)
       size     grams of the pack shown (used for the data attribute and label)
       variant  'wall' | 'hero' | 'thumb'  — wall has no own shadow (ShelfWall
                composites it into the plate); hero adds a soft studio shadow and
                a sheen; thumb is plain and lazy-loaded
       load     false holds the images back (the wall lazy-loads by bay)
       alt      accessible text; decorative (empty) by default because the
                surrounding button or row carries the name
     Emits `ready` once every image in the front has decoded (or failed). -->
<template>
  <span
    class="bag-front"
    :class="[`bag-front--${front?.kind || 'none'}`, `bag-front--${variant}`, front?.pouch ? `bag-front--${front.pouch}` : '', { 'bag-front--ready': done }]"
    :style="rootStyle"
    :data-grams="size || undefined"
    :role="alt ? 'img' : undefined"
    :aria-label="alt || undefined"
    :aria-hidden="alt ? undefined : 'true'"
  >
    <template v-if="front">
      <img
        class="bag-front__img"
        :src="load ? (small && front.small) || front.src : undefined"
        alt=""
        draggable="false"
        decoding="async"
        :loading="variant === 'thumb' ? 'lazy' : 'eager'"
        @load="tick"
        @error="tick"
      >
      <span v-if="front.sticker" class="bag-front__sticker" :class="{ 'bag-front__sticker--typed': front.sticker.typed }" :style="stickerStyle">
        <span v-if="front.sticker.typed" class="typed">
          <span class="typed__roaster">{{ front.sticker.typed.roaster }}</span>
          <span class="typed__name">{{ front.sticker.typed.name }}</span>
          <span class="typed__line">{{ front.sticker.typed.line }}</span>
        </span>
        <span v-else class="bag-front__photo"><img
          :src="load ? (small && front.sticker.small) || front.sticker.src : undefined"
          alt=""
          draggable="false"
          decoding="async"
          :loading="variant === 'thumb' ? 'lazy' : 'eager'"
          :style="photoStyle"
          @load="tick"
          @error="tick"
        ></span>
      </span>
      <span v-if="variant !== 'thumb'" class="bag-front__sheen" aria-hidden="true" />
    </template>
  </span>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { frontOf } from '../lib/wall-look.js';

const props = defineProps({
  coffee: { type: Object, default: null },
  size: { type: Number, default: 0 },
  variant: { type: String, default: 'thumb' },
  load: { type: Boolean, default: true },
  alt: { type: String, default: '' },
});
const emit = defineEmits(['ready']);

const front = computed(() => (props.coffee ? frontOf(props.coffee) : null));
// The wall and lists draw a bag ~100–150 px tall: the shelf-size copy is plenty.
const small = computed(() => props.variant === 'wall' || props.variant === 'thumb');
const need = computed(() => (front.value ? (front.value.sticker && !front.value.sticker.typed ? 2 : 1) : 0));
const seen = ref(0);
const done = computed(() => need.value > 0 && seen.value >= need.value);

watch(() => [front.value?.src, front.value?.sticker?.src, props.load], () => { seen.value = 0; });
watch(done, (d) => { if (d) emit('ready'); });
function tick () { seen.value += 1; }

const rootStyle = computed(() => ({ aspectRatio: String(front.value?.aspect || 0.72) }));
const photoStyle = computed(() => {
  const s = front.value?.sticker;
  if (!s || s.zoom === 1) return { objectPosition: `${s ? s.fx * 100 : 50}% ${s ? s.fy * 100 : 45}%` };
  return { transform: `scale(${s.zoom})`, transformOrigin: `${s.fx * 100}% ${s.fy * 100}%` };
});
const stickerStyle = computed(() => {
  const s = front.value?.sticker;
  if (!s) return null;
  return {
    left: `${s.x * 100}%`,
    top: `${s.y * 100}%`,
    width: `${s.w * 100}%`,
    height: `${s.h * 100}%`,
    transform: `rotate(${s.rot.toFixed(2)}deg)`,
  };
});
</script>

<style scoped>
.bag-front {
  position: relative;
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.bag-front__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
  opacity: 0;
  transition: opacity 240ms ease-out;
}
.bag-front--ready .bag-front__img,
.bag-front--ready .bag-front__sticker { opacity: 1; }
.bag-front--crop .bag-front__img { clip-path: inset(1.4% round 3px); }

/* The die-cut sticker: a white paper margin, rounded corners, pressed flat
   onto the pouch (a tight shadow, no lift) and lit by the same room light. */
.bag-front__sticker {
  position: absolute;
  box-sizing: border-box;
  padding: clamp(1px, 3.2%, 6px);
  background: var(--dock, #FAF7F1);
  border-radius: clamp(2px, 6%, 9px);
  box-shadow:
    0 0.5px 0.5px rgba(30, 20, 10, 0.28),
    0 1px 2px rgba(30, 20, 10, 0.16);
  overflow: hidden;
  opacity: 0;
  transition: opacity 240ms ease-out;
}
.bag-front__photo {
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: clamp(1px, 3%, 5px);
}
.bag-front__photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* A printed stock label: the roaster's own words, set small like a thermal print.
   Sized in container units so it reads the same on the wall and in the hero. */
.bag-front { container-type: inline-size; }
/* Typed labels are pure CSS/text, no image to wait on — render them at once
   rather than gate them behind the base pouch photo's load event (which, in a
   static render with no image network, would leave real printed text at
   opacity 0 forever). */
.bag-front__sticker--typed { background: var(--dock, #FAF7F1); padding: 0; opacity: 1; }
.typed {
  position: absolute;
  inset: 9% 8%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4cqw;
  color: var(--ink, #1F2A22);
  text-align: left;
  overflow: hidden;
}
.typed__roaster {
  font: 500 5.4cqw/1 var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-bottom: 0.6cqw solid currentColor;
  padding-bottom: 2.4cqw;
}
.typed__name {
  font: 800 9.4cqw/1.02 var(--font-display, 'Bricolage Grotesque', sans-serif);
  letter-spacing: -0.02em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.typed__line { font: 500 4.6cqw/1.2 var(--font-mono, 'Martian Mono', ui-monospace, monospace); opacity: 0.75; }

/* Room light across the label: a touch darker to the left, a matt sheen top right. */
.bag-front__sticker::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(40, 28, 16, 0.12), rgba(40, 28, 16, 0) 42%, rgba(255, 255, 255, 0.10) 80%, rgba(255, 255, 255, 0.02));
  pointer-events: none;
}
.bag-front--black .bag-front__sticker { background: var(--paper, #F4EFE6); }
/* The white stock pouch photographs a little lilac; bring it back to paper white. */
.bag-front--white .bag-front__img { filter: saturate(.3) brightness(1.02); }

/* Laminate sheen, the pouch catching the key light. */
.bag-front__sheen {
  position: absolute;
  inset: 1.5% 1.5% 30% 1.5%;
  border-radius: 3px;
  background: linear-gradient(105deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0) 38%);
  mix-blend-mode: screen;
  pointer-events: none;
}

.bag-front--hero { filter: drop-shadow(0 18px 18px rgba(40, 30, 20, 0.28)) drop-shadow(0 3px 4px rgba(40, 30, 20, 0.22)); }
.bag-front--thumb { filter: drop-shadow(0 2px 2px rgba(40, 30, 20, 0.22)); }

@media (prefers-reduced-motion: reduce) {
  .bag-front__img, .bag-front__sticker { transition: none; }
}
</style>
